import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Inject, LOCALE_ID } from '@angular/core';
import { PurchaseModel } from './purchase-model';
import { FormControl, FormGroup, Validators, FormArray } from '@angular/forms';

import { PurchaseService, Purchase } from '@schoolbelle/api/purchase';
import { Subscription, combineLatest, of } from 'rxjs';
import { GroupService, GroupExtraInfo, GroupAccessTokenDecoded } from '@schoolbelle/api/group';
import { map, switchMap, share, tap } from 'rxjs/operators';
import { LedgerService, LedgerSummary } from '@schoolbelle/api/ledger';

import { scrollTo, focusOn } from '@schoolbelle/common/functions';
import { get, isEqual, pick, forEachRight, times } from 'lodash-es';
import { UserToGroupService } from '@schoolbelle/api/user-to-group';

@Component({
  selector: 'app-order-tab',
  template: '',
  changeDetection:ChangeDetectionStrategy.OnPush,
})
export class PlaceOrderComponent implements OnInit {
  form:FormGroup;

  taxRate:FormControl;
  paymentCurrency:FormControl;
  paymentType:FormControl;
  toExistingGroup:FormControl;
  
  months:FormControl;
  startsAt:FormControl;
  expiresAt:FormControl;

  buyerInfo:FormGroup;

  purchaseType:FormControl;  
  accountId:FormControl;  
  groupType:FormControl;
  groupName:FormControl;
  groupAddress:FormControl;
  
  personInCharge:FormGroup;
  personInChargeEmail:FormControl;

  accountant:FormGroup;
  accountantEmail:FormControl;
  
  
  products:FormArray;
  
  message:FormControl;


  subscriptions = new Subscription();
  

  constructor( 
    protected group:GroupService,
    protected ledger:LedgerService,
    protected purchase:PurchaseService,
    protected utg:UserToGroupService,
    protected cdRef:ChangeDetectorRef,
    @Inject(LOCALE_ID) protected locale_id:string,
  ) { 
  }


  ngOnInit() {
    this.createControls();
    this.setValidators();
    this.createForm();
    this.fillInBasicValues();

    this.subscriptions.add(
      this.accountId.valueChanges.pipe(
        switchMap(account_id=>this.updateGroupRelatedDetails(account_id))
      ).subscribe(()=>{
        this.cdRef.detectChanges();
      })
    )
    this.subscriptions.add(
      this.startsAt.valueChanges.subscribe((startsAt)=>{
        this.products.controls.forEach((product:FormGroup)=>{
          const startsAtCtrl = product.get('startsAt');
          if (startsAtCtrl.value !== startsAt) startsAtCtrl.setValue(startsAt);
        });
      })
    )

    this.subscriptions.add(
      this.toExistingGroup.valueChanges.subscribe(bool=>{
        if (bool === false && this.accountId.value) this.accountId.setValue(null);
      })
    )

  }

  extractProdcutParams (type:'license'|'sms'|'crawl'):{productId, amount, quantity}[] {
    return this.products.controls.filter(c=>get(c.value, ['type']) === type).map(c=>{
      const {productId, amount, quantity} = c.value;
      return {
        productId, amount, quantity
      }
    })
  }
  updateWithProductParams (type:'license'|'sms'|'crawl', list:{productId:number, quantity?:number, amount?:number}[] = []) {
    forEachRight(this.products.controls, (c, index)=>{
      if (get(c.value, ['type']) === type) {
        this.products.removeAt(index);
      }
    })
    list.forEach(li=>{
      this.addProduct({...li, type});
    })
    
  }



  updateGroupRelatedDetails (account_id:string) {
    return of(null)
    .pipe(
      switchMap(()=>{
        const group_id:number = account_id ? Number(account_id.replace(/[\D]/g, '')) : null;
        if (group_id) return this.group.checkIn(group_id);
        else return of(null);
      }),
      switchMap(()=>{
        if (account_id) {
          return combineLatest(
            of(this.group.currentGroup),
            this.group.getExtraInfo(),
            this.purchase.list({type:'school'}),
            this.ledger.checkInForSummary({type:'school'})          
          );
        }
        else {
          return of([{}, {lat:null, lng:null, address:null}, [], {}] as [GroupAccessTokenDecoded , GroupExtraInfo, Purchase[], LedgerSummary]);
        }
      }),
      map(([group, exra_info, purchases, summary])=>{
        const purchaseType:'first'|'renewal' = purchases.length ? 'renewal' : 'first';
        const groupName = group.name || null;
        const groupAddress = pick(exra_info, ['lat', 'lng', 'address']);
        const startsAt = this.calculateStartsAt(summary.school_expires_at);
        return [purchaseType, groupName, groupAddress, startsAt];
      }),
      tap(([purchaseType, groupName, groupAddress, startsAt])=>{

        if (purchaseType !== this.purchaseType.value) this.purchaseType.setValue(purchaseType);
        if (groupName !== this.groupName.value) this.groupName.setValue(groupName);
        if(!isEqual(groupAddress, this.groupAddress.value)) this.groupAddress.setValue(groupAddress);
        if (this.startsAt.value !== startsAt) this.startsAt.setValue(startsAt);
      })
    )
    // .subscribe(([purchaseType, groupName, groupAddress, startsAt])=>{

    //   if (purchaseType !== this.purchaseType.value) this.purchaseType.setValue(purchaseType);
    //   if (groupName !== this.groupName.value) this.groupName.setValue(groupName);
    //   if(!isEqual(groupAddress, this.groupAddress.value)) this.groupAddress.setValue(groupAddress);
    //   if (this.startsAt.value !== startsAt) this.startsAt.setValue(startsAt);
    //   this.cdRef.detectChanges();
    // });
  }


  /**
   * 
   * @param params 
   * quantity is a newly added property to 
   * represent a number of monthly subscriptions at the same time(stacking) while amount for a monthly subscription means how many months worth of a subscription will be purchased.
   * 
   */
  addProduct (params:{type:'license'|'sms'|'crawl'|'discount', productId:number, amount?:number, quantity?:number, startsAt?:number, expiresAt?:number} = {type:'license', productId:null, amount:null, quantity:null, startsAt:null, expiresAt:null }) {
    const type = new FormControl(params.type, [Validators.required]); 
    const productId = new FormControl(params.productId, [Validators.required]); 
    const amount = new FormControl(params.amount, [Validators.required, Validators.min(1)]); 
    const quantity = new FormControl(params.quantity || 1, [Validators.required, Validators.min(1)]); 

    const productItem = this.purchase.currentProducts.find(product=>product.product_id === params.productId);
    const hasExpiringItems:boolean = productItem && productItem.data && productItem.data.find(li=>!['sms', 'discount'].includes(li.type)) ? true : false;
    const startsAt = new FormControl(hasExpiringItems ? params.startsAt || this.calculateStartsAt() : null);
    const expiresAt = new FormControl(hasExpiringItems ? params.expiresAt : null);
    const product = new FormGroup({type, productId, amount, quantity, startsAt, expiresAt});
    this.products.setControl(this.products.controls.length, product);
    return product;
  }

  createControls () {
    this.toExistingGroup = new FormControl(false);
    this.months = new FormControl();
    this.startsAt = new FormControl();
    this.expiresAt = new FormControl();

    this.paymentType = new FormControl('bankTransfer');

    this.paymentCurrency = new FormControl();
    this.taxRate = new FormControl(0.1);

    this.purchaseType = new FormControl(null);
    this.groupType = new FormControl(null);
    this.groupName = new FormControl(null);
    this.groupAddress = new FormControl(null);
    this.accountId = new FormControl(null);

    this.personInChargeEmail = new FormControl(null);

    this.accountantEmail = new FormControl(null);

    this.message = new FormControl(null);
    
  }
  setValidators () {
    this.paymentType.setValidators([Validators.required]);
    this.paymentCurrency.setValidators([Validators.required]);
    this.taxRate.setValidators([Validators.required]);

    switch(this.orderType) {
      case 'school':
        this.purchaseType.setValidators([Validators.required]);
        this.groupType.setValidators([Validators.required]);
        this.groupName.setValidators([Validators.required]);
        this.groupAddress.setValidators([Validators.required]);
        this.personInChargeEmail.setValidators([Validators.required, Validators.email]);
        break;
      case 'test':
        this.groupName.setValidators([Validators.required]);
        this.groupAddress.setValidators([Validators.required]);
        this.personInChargeEmail.setValidators([Validators.required, Validators.email]);
        break;

    }
    this.accountantEmail.setValidators([Validators.required, Validators.email]);
  }
  createForm () {
    this.buyerInfo = new FormGroup({
      groupType:this.groupType,
      purchaseType:this.purchaseType,
      groupName:this.groupName,
      groupAddress:this.groupAddress,
      accountId:this.accountId,
    })
    this.personInCharge = new FormGroup({
      personInChargeEmail:this.personInChargeEmail,
    })
    this.accountant = new FormGroup({
      accountantEmail:this.accountantEmail,
    })
    this.products = new FormArray([], (ctrl)=>{
      if (!ctrl.value.length) return {required:true};
      return null;
    });

    this.form = new FormGroup({
      toExistingGroup:this.toExistingGroup,
      months:this.months,
      startsAt:this.startsAt,
      expiresAt:this.expiresAt,
      paymentType:this.paymentType,
      paymentCurrency:this.paymentCurrency,
      taxRate:this.taxRate,

      buyerInfo : this.buyerInfo,
      personInCharge : this.personInCharge,
      accountant:this.accountant,
      products:this.products,
      message:this.message,
    });
  
  }

  emptyEnteredValues () {
    forEachRight(times(this.products.controls.length)).forEach(index=>this.products.removeAt(index)); 
    this.form.reset();

  }
  fillInBasicValues () {

    this.startsAt.setValue(this.calculateStartsAt());

    this.groupType.setValue('school.elementary');

    this.purchaseType.setValue('first'); // ????    

    this.paymentType.setValue('bankTransfer');

    const currency = (()=>{
      switch(this.locale_id.slice(0,2)) {
        case 'vi':
          return 'VND';
        case 'ko':
          return 'KRW';
        case 'en':
        default:
          return 'USD';       
      }
    })();
    this.paymentCurrency.setValue(currency);


    this.months.setValue(12); 
    
    const adminSchools = this.utg.schools.filter(school=>school.admin === true);
    this.toExistingGroup.setValue(adminSchools.length > 0); 


    const currentGroup = this.group.currentGroup;
    if (currentGroup) {
      this.groupName.setValue(currentGroup.name); 
      this.groupAddress.setValue(this.group.currentAddressData);
      
    }

    this.form.markAsPristine();

  }
  calculateStartsAt (last_expires_at = null):Number {
    // const last_expires_at = this.ledger.summary && this.ledger.summary.school_expires_at;    
    const starts_at_date = last_expires_at ? new Date(last_expires_at) : new Date();
    if (last_expires_at) {
      starts_at_date.setDate(starts_at_date.getDate() + 1);
    } else {
      if (starts_at_date.getMonth() < 2) starts_at_date.setMonth(2, 1);
    }
    starts_at_date.setHours(0,0,0,0);    
    return starts_at_date.getTime();
  }
  preview() {
    let w = 800;
    let h = 800;
    let el = document.querySelector('#quote');
    let dualScreenLeft = window.screenLeft != undefined ? window.screenLeft : (<any>screen).left;
    let dualScreenTop = window.screenTop != undefined ? window.screenTop : (<any>screen).top;

    let width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
    let height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;

    let left = ((width / 2) - (w / 2)) + dualScreenLeft;
    let top = ((height / 2) - (h / 2)) + dualScreenTop;

    let mywindow = window.open('', '_blank', 'scrollbars=1, height=' + h + ',width=' + w + ', top=' + top + ', left=' + left);
    if (mywindow) {
      mywindow.document.write('<html><head><title>견적서</title>');
      mywindow.document.write('<link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.1.3/css/bootstrap.min.css" integrity="sha384-MCw98/SFnGE8fJT3GXwEOngsV7Zt27NXFoaoApmYm81iuXoPkFOJwJ8ERdknLPMO" crossorigin="anonymous">');
      mywindow.document.write('</head><body style="overflow:scroll;">');
      mywindow.document.write(el.innerHTML);
      mywindow.document.write('</body></html>');
      mywindow.document.close(); // necessary for IE >= 10
    }
  }
  

  orderType:'school'|'sms'|'test'|'instruction'|'crawl' = 'school';
  buildParams (model:PurchaseModel) {
    const params = {
      buyer_name:model.buyerInfo.groupName,
      payment_type: model.paymentType,
      payment_currency: model.paymentCurrency,
      items:model.products.map((product)=>{
        return {
          product_id:product.productId,
          amount:product.amount,
          quantity:product.quantity,
        };
      }),
      type:this.orderType,
      extra_data:{
        group_name: model.buyerInfo.groupName,
        group_type: model.buyerInfo.groupType,
        group_address: model.buyerInfo.groupAddress,
        group_head_name: model.buyerInfo.groupHeadName,
        person_in_charge_email: model.personInCharge.personInChargeEmail,
        accountant_email: model.accountant && model.accountant.accountantEmail,
        lang:this.locale_id.slice(0,2),
      },
      starts_at:model.products[0] && model.products[0].startsAt,
      expires_at:model.products[0] && model.products[0].expiresAt,
      comment:model.message,
    };
    return params;
  }
  order () {
    const model:PurchaseModel = this.form.value;
    const params = this.buildParams(model);

    return this.purchase.placeOrder(params).pipe(
      tap(()=>{
        this.emptyEnteredValues();
        this.fillInBasicValues();
        this.cdRef.detectChanges();
      }),
    )
  }
  ngOnDestroy () {
    this.subscriptions.unsubscribe();
  }
  scrollToWrongInput () {
    const invalid = document.querySelector('.is-invalid');
    if (invalid) {
      focusOn(`.is-invalid`);
      scrollTo(`.is-invalid`, 300);
    }
  }
}